'use client';

import { ArrowUpRight, BookOpen, Martini, Utensils } from 'lucide-react';
import { useTranslations } from 'next-intl';
import type { SearchHit } from '../../search/types';

export default function KnowledgeSearchSuggestions({
  recommendedResults,
  onSelect
}: {
  recommendedResults: SearchHit[];
  onSelect: (query: string) => void;
}) {
  const t = useTranslations('knowledge-search');
  const titles = [
    ...new Set(
      recommendedResults
        .filter(hit => hit.kind === 'recipe')
        .map(hit => hit.title.trim())
        .filter(title => title.length >= 2 && title.length <= 200)
    )
  ].slice(0, 2);
  const cocktails = [...titles];
  for (const fallback of [t('sampleNegroni'), t('sampleOldFashioned')]) {
    if (
      cocktails.length < 2 &&
      !cocktails.some(
        title => title.toLocaleLowerCase() === fallback.toLocaleLowerCase()
      )
    )
      cocktails.push(fallback);
  }
  const suggestions = [
    ...cocktails.map((query, index) => ({
      query,
      category:
        index < titles.length ? t('sampleMenuItem') : t('sampleCocktails'),
      Icon: Martini
    })),
    {
      query: t('sampleServiceQuery'),
      category: t('sampleService'),
      Icon: BookOpen
    },
    { query: t('sampleFoodQuery'), category: t('sampleFood'), Icon: Utensils }
  ];

  return (
    <section aria-label={t('trySearch')} className="mt-8">
      <h3 className="text-section-title">{t('trySearch')}</h3>
      <p className="mt-1 text-caption text-muted-foreground">
        {t('sampleHint')}
      </p>
      <div className="mt-4 grid grid-cols-1 gap-2 lg:grid-cols-2">
        {suggestions.map(({ query, category, Icon }) => (
          <button
            key={query}
            type="button"
            onClick={() => onSelect(query)}
            className="flex min-h-16 items-center gap-3 rounded-xl bg-card px-4 py-3 text-left transition-colors duration-fast hover:bg-accent focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
          >
            <Icon
              className="size-5 shrink-0 text-muted-foreground"
              aria-hidden="true"
            />
            <span className="min-w-0 flex-1">
              <span className="block text-meta text-muted-foreground">
                {category}
              </span>
              <span className="block break-words text-body font-medium">
                {query}
              </span>
            </span>
            <ArrowUpRight
              className="size-4 shrink-0 text-muted-foreground"
              aria-hidden="true"
            />
          </button>
        ))}
      </div>
    </section>
  );
}
